<template>
  <div
    v-if="variant === 'sidebar' || tasks.length"
    class="day-tasks"
    :class="variant"
    dir="rtl"
  >
    <div class="flex justify-between items-center gap-2">
      <span v-if="variant === 'card'" class="text-[15px] font-bold text-surface-900">
        {{ heading }} <span v-if="openCount" class="font-medium text-surface-500">· {{ openCount }} פתוחות</span>
      </span>
      <span v-else class="text-[10px] font-bold uppercase tracking-widest text-gray-400">{{ heading }}{{ openCount ? ` · ${openCount}` : '' }}</span>
      <router-link :to="{ name: AdminRouteNames.Tasks }" class="text-[13px] font-semibold text-primary-700 no-underline flex-none">
        לכל המשימות
      </router-link>
    </div>

    <div class="flex flex-col" :class="variant === 'card' ? 'gap-0.5' : ''">
      <TaskRow
        v-for="task in shown"
        :key="task.id"
        :task="task"
        compact
        :busy="busyId === task.id"
        @toggle="toggle"
        @open="edit"
      />
    </div>

    <div class="flex items-center gap-3">
      <button v-if="hidden" type="button" class="text-[13px] text-surface-600" @click="expanded = true">ועוד {{ hidden }}</button>
      <button
        v-if="variant === 'sidebar' || canAdd"
        type="button"
        class="add-btn"
        :class="{ 'flex-1': variant === 'sidebar' }"
        @click="add"
      ><i class="pi pi-plus text-xs" /> משימה</button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { DateTime } from 'luxon';
import { useToast } from 'vue-toastification';
import { type ITask, TaskStatusEnum } from '@torim-app/types';
import { useAdminTasksStore } from '@app/stores';
import { TasksApi } from '@app/api/admin/tasks-api';
import { AdminRouteNames } from '@app/router/route-names';
import { trackEvent } from '@app/composables/posthog';
import TaskRow from './TaskRow.vue';
import { todayIso } from './task-utils';
import { useTaskEditor } from './use-task-editor';

const props = withDefaults(defineProps<{
  date: Date;
  /** `card` above the mobile calendar, `sidebar` in the desktop calendar's side column. */
  variant?: 'card' | 'sidebar';
  canAdd?: boolean;
  /** The employees the calendar is filtered to; omitted = my own tasks. */
  assigneeIds?: string[];
}>(), { variant: 'card', canAdd: true });

const tasksStore = useAdminTasksStore();
const toast = useToast();
const { openTaskEditor } = useTaskEditor();

const tasks = ref<ITask[]>([]);
const busyId = ref<string>();
const expanded = ref(false);
const LIMIT = 4;

const iso = computed(() => DateTime.fromJSDate(props.date).setZone('Asia/Jerusalem').toISODate() as string);
const heading = computed(() => {
  if (iso.value === todayIso()) return 'משימות להיום';
  return `משימות ל${DateTime.fromISO(iso.value).setLocale('he').toFormat('cccc d.M')}`;
});
const openCount = computed(() => tasks.value.filter(t => t.status === TaskStatusEnum.Open).length);
const shown = computed(() => (expanded.value ? tasks.value : tasks.value.slice(0, LIMIT)));
const hidden = computed(() => tasks.value.length - shown.value.length);

const load = async () => {
  try {
    tasks.value = await TasksApi.listForDay(iso.value, props.assigneeIds);
  } catch (e) {
    console.error(e);
  }
};

const toggle = async (task: ITask) => {
  if (!task.id || busyId.value) return;
  busyId.value = task.id;
  const done = task.status !== TaskStatusEnum.Done;
  // Tick in place right away; the reload that follows keeps it in the list as done.
  task.status = done ? TaskStatusEnum.Done : TaskStatusEnum.Open;
  try {
    await tasksStore.setDone(task.id, done);
    trackEvent('task_toggled', { done, surface: `calendar-${props.variant}` });
  } catch (e) {
    console.error(e);
    task.status = done ? TaskStatusEnum.Open : TaskStatusEnum.Done;
    toast.error('לא הצלחנו לעדכן את המשימה');
  } finally {
    busyId.value = undefined;
  }
};

const edit = (task: ITask) => openTaskEditor({ task });
// Filtered to one employee: a new task goes to them.
const add = () => openTaskEditor({
  defaults: { dueDate: iso.value, ...(props.assigneeIds?.length === 1 ? { assigneeId: props.assigneeIds[0] } : {}) },
});

watch(iso, () => {
  expanded.value = false;
  load();
}, { immediate: true });
watch(() => tasksStore.version, load);
watch(() => props.assigneeIds?.join(','), load);

defineExpose({ reload: load });
</script>

<style scoped lang="scss">
.day-tasks {
  @apply flex flex-col gap-2;

  &.card {
    @apply bg-white border border-surface-200 rounded-2xl p-3;
  }
}

.add-btn {
  @apply h-9 px-3 rounded-[10px] text-[13px] font-semibold text-primary-700 bg-white flex items-center justify-center gap-1.5;
  border: 1.5px dashed #b6a8e6;
}
</style>
